import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '@/api/client'
import type { ConventionDto, ConventionIdentity } from '@/api/types'

// Public convention identity (dates / year / duration / edition), loaded ONCE from
// GET /api/v1/convention. The raw wire shape is ConventionDto; the display copy the masthead,
// fact rail, and footer bind (dateRange, edition, …) is DERIVED here so there is exactly one
// place that turns "2027-01-29 → 2027-01-31" into "Jan 29–31, 2027". Anonymous-safe; degrades to
// null on failure (views fall back to the coded placeholder copy — "Winter War 53").

const MONTH = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function parseDate(iso: string | null): Date | null {
  if (!iso) return null
  // The API sends slot dates as date-only strings ("2027-01-29"). new Date() would parse those
  // as UTC midnight, and the local getDate()/getMonth() below then read them a day early in any
  // timezone west of UTC. Build a LOCAL-midnight Date from the parts so the calendar day is exact.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "Jan 29–31, 2027" (same month), "Jan 31 – Feb 2, 2027" (spanning), or '' when unknown. */
function formatDateRange(start: Date | null, end: Date | null): string {
  if (!start) return ''
  const sm = MONTH[start.getMonth()]
  if (!end || end.toDateString() === start.toDateString()) {
    return `${sm} ${start.getDate()}, ${start.getFullYear()}`
  }
  if (end.getMonth() === start.getMonth() && end.getFullYear() === start.getFullYear()) {
    return `${sm} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`
  }
  return `${sm} ${start.getDate()} – ${MONTH[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`
}

/** Derive the display identity from the wire DTO. Exported for tests. */
export function deriveIdentity(dto: ConventionDto): ConventionIdentity {
  const start = parseDate(dto.startDate)
  const end = parseDate(dto.endDate)
  const durationDays =
    start && end
      ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1)
      : null

  // "Winter War 53" carries its edition number in the name; fall back to the conID.
  const nameNumber = dto.name ? /(\d+)\s*$/.exec(dto.name.trim())?.[1] : undefined
  const editionNumber = nameNumber ? Number(nameNumber) : (dto.conventionId ?? null)
  const edition = dto.name ?? (editionNumber != null ? `Winter War ${editionNumber}` : '')

  const durationWord =
    durationDays === 3
      ? 'Three days'
      : durationDays === 2
        ? 'Two days'
        : durationDays === 1
          ? 'One day'
          : ''

  return {
    name: dto.name,
    displayName: dto.name,
    startDate: dto.startDate,
    endDate: dto.endDate,
    year: dto.year ?? (start ? start.getFullYear() : null),
    durationDays,
    durationWord,
    dateRange: formatDateRange(start, end),
    editionNumber,
    edition,
    editionName: edition,
  }
}

export const useConventionStore = defineStore('convention', () => {
  const raw = ref<ConventionDto | null>(null)
  const convention = ref<ConventionIdentity | null>(null)
  const loaded = ref(false)

  // Dedupe concurrent startup callers (App.vue mount + router guard) onto one request.
  let inflight: Promise<void> | null = null

  function load(force = false): Promise<void> {
    if (loaded.value && !force) return Promise.resolve()
    if (inflight) return inflight
    inflight = (async () => {
      try {
        raw.value = await api.get<ConventionDto>('/api/v1/convention')
        convention.value = raw.value ? deriveIdentity(raw.value) : null
      } catch {
        raw.value = null
        convention.value = null
      } finally {
        loaded.value = true
        inflight = null
      }
    })()
    return inflight
  }

  return { raw, convention, loaded, load }
})
